/**
 * MilestoneList - Renders a list of MilestoneCard components
 *
 * Displays all milestones for a project with their individual states.
 * Shows an EmptyState component when the list is empty.
 *
 * Mirrors the EJS partial milestones/info/_showMilestones.ejs.
 */

import { cn } from '@lib/cn';

import { MilestoneCard } from '@components/features/milestones/MilestoneCard';
import { MilestoneActions } from '@components/features/milestones/MilestoneActions';
import { useDaoConfig } from '@/hooks/useDaoConfig';
import type { Milestone, User } from '@/types/index';

interface MilestoneListProps {
  milestones: Milestone[];
  allDeliveryTimes?: Array<{ id?: number; description: string }>;
  /** Project ID for milestone actions. */
  projectId?: string;
  /** Authenticated user for milestone actions. */
  user?: User;
  /** Whether the user is the client for this project. */
  isClient?: boolean;
  /** Whether the user is the consultant for this project. */
  isConsultant?: boolean;
  className?: string;
}

export function MilestoneList({
  milestones,
  allDeliveryTimes,
  projectId,
  user,
  isClient = false,
  isConsultant = false,
  className,
}: MilestoneListProps) {
  const { currency } = useDaoConfig();
  const containerClass = className
    ? `${className} projDetailSection`
    : 'projDetailSection';

  if (milestones.length === 0) {
    return (
      <div className={containerClass}>
        {/* Header Section */}
        <div className="flex flex-col items-start text-left">
          <h3 className="sectionTitle !font-normal !text-[--state-brand-active]">
            Milestones
          </h3>
          <p className="sectionSubtitle">
            {isConsultant
              ? 'Review the project Scope and define clear milestones and budgets.'
              : 'Break down the project scope in milestones that the client can review and approve.'}
          </p>
          <p className="mt-1 text-sm text-[--text-dark-secondary]">
            Planning template. Milestones are not defined yet.
          </p>
        </div>

        {/* Milestones Box Container - NO MILESTONES - TO DO */}
        <div
          className="sectionContent milestoneList !px-6 !py-4"
          style={{ borderRadius: 8 }}
        >
          {Array.from({ length: 4 }).map((_, index) => (
            <div
              key={index}
              className={cn(
                'flex h-fit gap-[--spacing-8] border-0 bg-transparent p-0 text-left',
                className
              )}
            >
              {/* Progress indicator dot */}
              <div className="flex flex-col items-center pt-2">
                <div className="z-10 h-[7px] w-[6px] rounded-full bg-transparent outline outline-[6px] outline-offset-1 outline-[--base-fill-3]" />
                <div
                  className={cn(
                    'h-full w-px',
                    index == 3 ? 'bg-transparent' : 'bg-[--base-border]'
                  )}
                />
              </div>
              {/* Header row: number, title, budget, state */}
              <div className="w-full p-0 pb-[--spacing-8]">
                <div className="flex flex-wrap items-center gap-[--spacing-5] text-[--text-dark-tertiary]">
                  <h4 className="flex-1 text-base font-normal">
                    Milestone {index + 1}
                  </h4>
                  <p className="text-xs text-[--text-dark-primary]">{currency.symbol} —</p>
                  <p className="text-xs">Delivery time</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const showActions = !!projectId && !!user;

  return (
    <div className={containerClass}>
      <h3 className="sectionTitle">Milestones</h3>
      <div className="sectionContent milestoneList">
        {milestones.map((milestone, index) => {
          {
            /* Calculate if this is the last milestone for dynamic styling */
          }
          const isLast = index === milestones.length - 1;

          return (
            <div key={milestone.id ?? `draft-${index}`} className="flex h-fit">
              <MilestoneCard
                milestone={milestone}
                index={index}
                allDeliveryTimes={allDeliveryTimes}
                isLast={isLast} // 👈 lo pasas aquí
              />

              {showActions && (
                <MilestoneActions
                  milestone={milestone}
                  projectId={projectId}
                  user={user}
                  isClient={isClient}
                  isConsultant={isConsultant}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
